'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BtuCalculator() {
  const t = useTranslations("tools.btuCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // AC Calculator State
  const [acInputs, setAcInputs] = useState({
    size: 150,
    sizeUnit: 'feet',
    ceiling: 9,
    ceilingUnit: 'feet',
    people: 2,
    roomType: 'bedroom',
    insulation: 'normal',
    sun: 'normal',
    climate: 'normal'
  });

  // General Calculator State
  const [genInputs, setGenInputs] = useState({
    width: 12,
    widthUnit: 'feet',
    length: 12,
    lengthUnit: 'feet',
    ceiling: 9,
    ceilingUnit: 'feet',
    insulation: 'normal',
    tempChange: 50,
    tempUnit: 'f'
  });

  const [acResults, setAcResults] = useState<any>(null);
  const [genResults, setGenResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const convertToFeet = (value: number, unit: string) => {
    return unit === 'meters' ? value * 3.28084 : value;
  };

  const convertToSquareFeet = (value: number, unit: string) => {
    return unit === 'meters' ? value * 10.7639 : value;
  };

  const handleAcCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const sqFeet = convertToSquareFeet(acInputs.size, acInputs.sizeUnit);
    const ceilingFeet = convertToFeet(acInputs.ceiling, acInputs.ceilingUnit);
    const volume = sqFeet * ceilingFeet;

    let baseBTU = sqFeet * 20;

    if (ceilingFeet > 8) {
      baseBTU *= 1.15;
    }

    baseBTU += acInputs.people * 600;

    const roomTypeMultiplier: { [key: string]: number } = {
      'bedroom': 1.0,
      'living': 1.1,
      'kitchen': 1.3,
      'house': 1.2,
      'first': 1.15,
      'second': 1.05
    };
    baseBTU *= roomTypeMultiplier[acInputs.roomType] || 1.0;

    const insulationMultiplier: { [key: string]: number } = {
      'good': 0.9,
      'normal': 1.0,
      'poor': 1.2
    };
    baseBTU *= insulationMultiplier[acInputs.insulation];

    const sunMultiplier: { [key: string]: number } = {
      'shaded': 0.9,
      'normal': 1.0,
      'sunny': 1.15
    };
    baseBTU *= sunMultiplier[acInputs.sun];

    const climateMultiplier: { [key: string]: number } = {
      'cold': 0.95,
      'normal': 1.0,
      'hot': 1.15
    };
    baseBTU *= climateMultiplier[acInputs.climate];

    const finalBTU = Math.round(baseBTU);
    const tons = (finalBTU / 12000).toFixed(2);
    const watts = Math.round(finalBTU / 3.412);

    let recommendation = '';
    if (finalBTU < 6000) {
      recommendation = 'smallUnit';
    } else if (finalBTU < 12000) {
      recommendation = 'mediumUnit';
    } else if (finalBTU < 18000) {
      recommendation = 'largeUnit';
    } else {
      recommendation = 'centralSystem';
    }

    setAcResults({
      btu: finalBTU,
      volume: Math.round(volume),
      tons,
      watts,
      recommendation
    });

    setGenResults(null);
  };

  const handleGenCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const widthFeet = convertToFeet(genInputs.width, genInputs.widthUnit);
    const lengthFeet = convertToFeet(genInputs.length, genInputs.lengthUnit);
    const ceilingFeet = convertToFeet(genInputs.ceiling, genInputs.ceilingUnit);
    const volume = widthFeet * lengthFeet * ceilingFeet;

    const tempChangeF = genInputs.tempUnit === 'c' ? (genInputs.tempChange * 9/5) : genInputs.tempChange;

    let btuPerCuFt = 0.133;

    const insulationFactor: { [key: string]: number } = {
      'good': 0.85,
      'normal': 1.0,
      'poor': 1.25
    };
    btuPerCuFt *= insulationFactor[genInputs.insulation];

    const totalBTU = Math.round(volume * btuPerCuFt * tempChangeF);
    const watts = Math.round(totalBTU / 3.412);
    const kwhPerDay = ((watts * 24) / 1000).toFixed(2);

    let recommendation = '';
    if (tempChangeF > 60) {
      recommendation = 'highTempChange';
    } else if (genInputs.insulation === 'poor') {
      recommendation = 'poorInsulation';
    } else {
      recommendation = 'efficientSetup';
    }

    setGenResults({
      btu: totalBTU,
      volume: Math.round(volume),
      watts,
      kwhPerDay,
      recommendation
    });

    setAcResults(null);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    h3: getResponsiveStyle(
      { color: '#2563eb', marginBottom: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: getResponsiveStyle(
      { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.8rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        backgroundColor: 'white'
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.8rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1rem' }
    ),
    inputRow: getResponsiveStyle(
      { display: 'grid', gap: '0.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr auto' },
      { gridTemplateColumns: '1fr auto' }
    ),
    selectInRow: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        backgroundColor: 'white'
      },
      { width: '100%', padding: '0.75rem', fontSize: '1rem' },
      { minWidth: '140px', padding: '0.8rem', fontSize: '1rem' },
      { minWidth: '140px', padding: '0.875rem', fontSize: '1rem' }
    ),
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1.125rem 2rem', fontSize: '1.125rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultHighlight: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
        color: 'white',
        borderRadius: '0.75rem',
        textAlign: 'center' as const,
        marginBottom: '1.5rem'
      },
      { padding: '1.25rem' },
      { padding: '1.375rem' },
      { padding: '1.5rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, display: 'block', marginBottom: '0.5rem' },
      { fontSize: '2rem' },
      { fontSize: '2.25rem' },
      { fontSize: '2.5rem' }
    ),
    resultLabel: getResponsiveStyle(
      { opacity: 0.95 },
      { fontSize: '0.875rem' },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' }
    ),
    resultDetails: getResponsiveStyle(
      { display: 'grid', gap: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
    ),
    resultItem: {
      background: '#f8fafc',
      padding: '1rem',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb'
    },
    resultItemLabel: {
      display: 'block',
      fontSize: '0.875rem',
      color: '#64748b',
      marginBottom: '0.25rem'
    },
    resultItemValue: getResponsiveStyle(
      { display: 'block', fontWeight: 600, color: '#1e293b' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' },
      { fontSize: '1.25rem' }
    ),
    infoText: {
      fontSize: '0.875rem',
      color: '#64748b',
      marginTop: '0.25rem'
    },
    recommendation: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        borderLeft: '4px solid #10b981',
        borderRadius: '0.5rem',
        marginTop: '1.5rem'
      },
      { padding: '0.875rem' },
      { padding: '0.9375rem' },
      { padding: '1rem' }
    ),
    recommendationTitle: {
      color: '#065f46',
      marginBottom: '0.5rem',
      fontSize: '1rem',
      fontWeight: 600
    },
    recommendationText: {
      color: '#047857',
      margin: 0,
      fontSize: '0.95rem'
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    ),
    refList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    refItem: {
      marginBottom: '0.75rem'
    },
    refLink: getResponsiveStyle(
      { color: colors.primary, textDecoration: 'none', transition: 'color 0.3s ease' },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    )
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.h2}>{t("toolTitle")}</h2>
        </div>

        <div style={styles.calculatorGrid}>
          <div style={styles.calculatorCard}>
            <h3 style={styles.h3}>❄️ {t("acCalculatorTitle")}</h3>
            <form onSubmit={handleAcCalculate}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ac-size">{t("roomSize")}</label>
                <div style={styles.inputRow}>
                  <input
                    style={styles.input}
                    type="number"
                    id="ac-size"
                    value={acInputs.size}
                    onChange={(e) => setAcInputs({...acInputs, size: parseFloat(e.target.value)})}
                    min="1"
                    step="1"
                    required
                  />
                  <select
                    style={styles.selectInRow}
                    value={acInputs.sizeUnit}
                    onChange={(e) => setAcInputs({...acInputs, sizeUnit: e.target.value})}
                  >
                    <option value="feet">{t("squareFeet")}</option>
                    <option value="meters">{t("squareMeters")}</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ac-ceiling">{t("ceilingHeight")}</label>
                <div style={styles.inputRow}>
                  <input
                    style={styles.input}
                    type="number"
                    id="ac-ceiling"
                    value={acInputs.ceiling}
                    onChange={(e) => setAcInputs({...acInputs, ceiling: parseFloat(e.target.value)})}
                    min="1"
                    step="0.1"
                    required
                  />
                  <select
                    style={styles.selectInRow}
                    value={acInputs.ceilingUnit}
                    onChange={(e) => setAcInputs({...acInputs, ceilingUnit: e.target.value})}
                  >
                    <option value="feet">{t("feet")}</option>
                    <option value="meters">{t("meters")}</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ac-people">{t("numberOfPeople")}</label>
                <input
                  style={styles.input}
                  type="number"
                  id="ac-people"
                  value={acInputs.people}
                  onChange={(e) => setAcInputs({...acInputs, people: parseInt(e.target.value)})}
                  min="0"
                  step="1"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ac-room-type">{t("roomType")}</label>
                <select
                  style={styles.select}
                  id="ac-room-type"
                  value={acInputs.roomType}
                  onChange={(e) => setAcInputs({...acInputs, roomType: e.target.value})}
                >
                  <option value="bedroom">{t("bedroom")}</option>
                  <option value="living">{t("livingRoom")}</option>
                  <option value="kitchen">{t("kitchen")}</option>
                  <option value="house">{t("entireHouse")}</option>
                  <option value="first">{t("firstFloor")}</option>
                  <option value="second">{t("secondFloor")}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ac-insulation">{t("insulationCondition")}</label>
                <select
                  style={styles.select}
                  id="ac-insulation"
                  value={acInputs.insulation}
                  onChange={(e) => setAcInputs({...acInputs, insulation: e.target.value})}
                >
                  <option value="good">{t("goodInsulation")}</option>
                  <option value="normal">{t("averageInsulation")}</option>
                  <option value="poor">{t("poorInsulation")}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ac-sun">{t("sunExposure")}</label>
                <select
                  style={styles.select}
                  id="ac-sun"
                  value={acInputs.sun}
                  onChange={(e) => setAcInputs({...acInputs, sun: e.target.value})}
                >
                  <option value="shaded">{t("heavilyShaded")}</option>
                  <option value="normal">{t("averageSun")}</option>
                  <option value="sunny">{t("verySunny")}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ac-climate">{t("climate")}</label>
                <select
                  style={styles.select}
                  id="ac-climate"
                  value={acInputs.climate}
                  onChange={(e) => setAcInputs({...acInputs, climate: e.target.value})}
                >
                  <option value="cold">{t("coldClimate")}</option>
                  <option value="normal">{t("averageClimate")}</option>
                  <option value="hot">{t("hotClimate")}</option>
                </select>
              </div>

              <button type="submit" style={styles.calculateBtn}>{t("calculateAcBtn")}</button>
            </form>
          </div>

          <div style={styles.calculatorCard}>
            <h3 style={styles.h3}>🔥 {t("generalCalculatorTitle")}</h3>
            <form onSubmit={handleGenCalculate}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="gen-width">{t("roomWidth")}</label>
                <div style={styles.inputRow}>
                  <input
                    style={styles.input}
                    type="number"
                    id="gen-width"
                    value={genInputs.width}
                    onChange={(e) => setGenInputs({...genInputs, width: parseFloat(e.target.value)})}
                    min="1"
                    step="0.1"
                    required
                  />
                  <select
                    style={styles.selectInRow}
                    value={genInputs.widthUnit}
                    onChange={(e) => setGenInputs({...genInputs, widthUnit: e.target.value})}
                  >
                    <option value="feet">{t("feet")}</option>
                    <option value="meters">{t("meters")}</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="gen-length">{t("roomLength")}</label>
                <div style={styles.inputRow}>
                  <input
                    style={styles.input}
                    type="number"
                    id="gen-length"
                    value={genInputs.length}
                    onChange={(e) => setGenInputs({...genInputs, length: parseFloat(e.target.value)})}
                    min="1"
                    step="0.1"
                    required
                  />
                  <select
                    style={styles.selectInRow}
                    value={genInputs.lengthUnit}
                    onChange={(e) => setGenInputs({...genInputs, lengthUnit: e.target.value})}
                  >
                    <option value="feet">{t("feet")}</option>
                    <option value="meters">{t("meters")}</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="gen-ceiling">{t("ceilingHeight")}</label>
                <div style={styles.inputRow}>
                  <input
                    style={styles.input}
                    type="number"
                    id="gen-ceiling"
                    value={genInputs.ceiling}
                    onChange={(e) => setGenInputs({...genInputs, ceiling: parseFloat(e.target.value)})}
                    min="1"
                    step="0.1"
                    required
                  />
                  <select
                    style={styles.selectInRow}
                    value={genInputs.ceilingUnit}
                    onChange={(e) => setGenInputs({...genInputs, ceilingUnit: e.target.value})}
                  >
                    <option value="feet">{t("feet")}</option>
                    <option value="meters">{t("meters")}</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="gen-insulation">{t("insulationCondition")}</label>
                <select
                  style={styles.select}
                  id="gen-insulation"
                  value={genInputs.insulation}
                  onChange={(e) => setGenInputs({...genInputs, insulation: e.target.value})}
                >
                  <option value="good">{t("goodInsulation")}</option>
                  <option value="normal">{t("averageInsulation")}</option>
                  <option value="poor">{t("poorInsulation")}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="gen-temp">{t("desiredTempChange")}</label>
                <div style={styles.inputRow}>
                  <input
                    style={styles.input}
                    type="number"
                    id="gen-temp"
                    value={genInputs.tempChange}
                    onChange={(e) => setGenInputs({...genInputs, tempChange: parseFloat(e.target.value)})}
                    min="1"
                    step="1"
                    required
                  />
                  <select
                    style={styles.selectInRow}
                    value={genInputs.tempUnit}
                    onChange={(e) => setGenInputs({...genInputs, tempUnit: e.target.value})}
                  >
                    <option value="f">{t("fahrenheit")}</option>
                    <option value="c">{t("celsius")}</option>
                  </select>
                </div>
                <div style={styles.infoText}>{t("tempChangeExample")}</div>
              </div>

              <button type="submit" style={styles.calculateBtn}>{t("calculateBtn")}</button>
            </form>
          </div>
        </div>

        {acResults && (
          <div style={styles.results}>
            <h3 style={styles.h2}>{t("acResultsTitle")}</h3>
            <div style={styles.resultHighlight}>
              <span style={styles.resultValue}>{acResults.btu.toLocaleString()}</span>
              <span style={styles.resultLabel}>{t("btuPerHour")}</span>
            </div>
            <div style={styles.resultDetails}>
              <div style={styles.resultItem}>
                <span style={styles.resultItemLabel}>{t("roomVolume")}</span>
                <span style={styles.resultItemValue}>{acResults.volume.toLocaleString()} {t("cubicFeet")}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultItemLabel}>{t("recommendedAcSize")}</span>
                <span style={styles.resultItemValue}>{acResults.tons} {t("tons")}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultItemLabel}>{t("powerWatts")}</span>
                <span style={styles.resultItemValue}>{acResults.watts.toLocaleString()} W</span>
              </div>
            </div>
            <div style={styles.recommendation}>
              <h4 style={styles.recommendationTitle}>💡 {t("recommendation")}</h4>
              <p style={styles.recommendationText}>{t(acResults.recommendation)}</p>
            </div>
          </div>
        )}

        {genResults && (
          <div style={styles.results}>
            <h3 style={styles.h2}>{t("generalResultsTitle")}</h3>
            <div style={styles.resultHighlight}>
              <span style={styles.resultValue}>{genResults.btu.toLocaleString()}</span>
              <span style={styles.resultLabel}>{t("btuPerHour")}</span>
            </div>
            <div style={styles.resultDetails}>
              <div style={styles.resultItem}>
                <span style={styles.resultItemLabel}>{t("roomVolume")}</span>
                <span style={styles.resultItemValue}>{genResults.volume.toLocaleString()} {t("cubicFeet")}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultItemLabel}>{t("powerWatts")}</span>
                <span style={styles.resultItemValue}>{genResults.watts.toLocaleString()} W</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultItemLabel}>{t("energyPerDay")}</span>
                <span style={styles.resultItemValue}>{genResults.kwhPerDay} kWh</span>
              </div>
            </div>
            <div style={styles.recommendation}>
              <h4 style={styles.recommendationTitle}>💡 {t("recommendation")}</h4>
              <p style={styles.recommendationText}>{t(genResults.recommendation)}</p>
            </div>
          </div>
        )}
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("step1Title")}</span> {t("step1Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step2Title")}</span> {t("step2Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step3Title")}</span> {t("step3Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step4Title")}</span> {t("step4Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step5Title")}</span> {t("step5Description")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insight1")}</li>
          <li style={styles.listItem}>{t("insight2")}</li>
          <li style={styles.listItem}>{t("insight3")}</li>
          <li style={styles.listItem}>{t("insight4")}</li>
          <li style={styles.listItem}>{t("insight5")}</li>
          <li style={styles.listItem}>{t("insight6")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <p style={styles.paragraph}>{t("detailsParagraph1")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph2")}</p>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem'}}>{t("factorsTitle")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("factor1Title")}</span> {t("factor1Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("factor2Title")}</span> {t("factor2Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("factor3Title")}</span> {t("factor3Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("factor4Title")}</span> {t("factor4Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("factor5Title")}</span> {t("factor5Description")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq1Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq2Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq3Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq4Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq5Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.refList}>
          <li style={styles.refItem}>
            <a
              href="https://www.logan-inc.com/blog/btu-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              BTU Size Calculator | Logan Services A/C, Heat & Plumbing
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.calculator.net/btu-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              BTU Calculator - Calculator.net
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.goairflow.com/blog/what-is-btu/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              Understanding BTU: The Key to Efficient Heating & Cooling
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.eds.tech/what-is-the-hvac-btu-calculator-how-does-it-work/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              What is the HVAC BTU Calculator? How does it Work? - EDS
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://en.wikipedia.org/wiki/British_thermal_unit"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              British thermal unit - Wikipedia
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}